/**
 * Surviving a page reload mid-run (2026-10-15).
 *
 * A reload used to forfeit the run outright. `main.ts` tears the scene down on
 * `beforeunload`, the teardown called `room.leave()`, and a consented leave is
 * recorded as a loss on the spot (run-lifecycle.md Core Rule #2) — the player
 * landed back in the hub with the delve marked DIED. Verified live: a Vite
 * full reload (any edit to a non-HMR module in dev) at 08:12:47 ended a party
 * run 7.6s after it started, and from the player's side it reads exactly as
 * "the room never got created". A real refresh, or a phone discarding a
 * backgrounded tab, did the same thing in production. `main.ts` saves on both
 * `beforeunload` and `pagehide` — the latter is the event mobile browsers
 * actually fire for a discarded or swiped-away tab. A seat is not saved while
 * the player is downed: reloading then is a forfeit, not a pause.
 *
 * The server already holds a dropped seat for `DISCONNECT_GRACE_MS`
 * (`allowReconnection`); nothing ever used it from a browser that had
 * reloaded, because the token that reclaims the seat lived only in memory.
 * This keeps it in `sessionStorage` — per tab, so a second tab never steals
 * the first one's seat — together with when it was written, so a stale token
 * is never tried after the server has already let the seat go.
 */

const KEY = 'hullrot.run-resume';

/**
 * How long after the page went away a saved seat is still worth trying.
 * A little under the server's grace (`NET.DISCONNECT_GRACE_MS`, 10s): a
 * reconnect that races the deadline fails anyway, and failing fast drops the
 * player into the hub instead of a loading screen. A dev reload measured 3.1-3.4s
 * from unload to back in the seat.
 */
export const RESUME_WINDOW_MS = 9_000;

export interface SavedRun {
  token: string;
  /** `Date.now()` when the page let go of the room. */
  savedAt: number;
}

export function isResumable(saved: SavedRun | null, now: number, windowMs = RESUME_WINDOW_MS): saved is SavedRun {
  if (!saved || typeof saved.token !== 'string' || saved.token === '') return false;
  if (typeof saved.savedAt !== 'number' || !Number.isFinite(saved.savedAt)) return false;
  const age = now - saved.savedAt;
  return age >= 0 && age <= windowMs;
}

export function parseSavedRun(raw: string | null): SavedRun | null {
  if (!raw) return null;
  try {
    const v = JSON.parse(raw) as Partial<SavedRun>;
    if (typeof v.token !== 'string' || typeof v.savedAt !== 'number') return null;
    return { token: v.token, savedAt: v.savedAt };
  } catch {
    return null;
  }
}

/** Every access is guarded: storage can be absent or throw (private mode, blocked site data). */
export function saveRunForResume(token: string, now = Date.now()): void {
  try { sessionStorage.setItem(KEY, JSON.stringify({ token, savedAt: now } satisfies SavedRun)); } catch { /* no storage: a reload simply ends the run, as before */ }
}

export function clearSavedRun(): void {
  try { sessionStorage.removeItem(KEY); } catch { /* nothing to clear */ }
}

/** The saved seat if it is still worth trying, consuming it either way — a token is good for one attempt. */
export function takeResumableRun(now = Date.now()): SavedRun | null {
  let raw: string | null = null;
  try { raw = sessionStorage.getItem(KEY); } catch { return null; }
  clearSavedRun();
  const saved = parseSavedRun(raw);
  return isResumable(saved, now) ? saved : null;
}

/** Whether a reload is about to try its seat, without spending the token — for the boot screen's copy. */
export function peekResumableRun(now = Date.now()): boolean {
  let raw: string | null = null;
  try { raw = sessionStorage.getItem(KEY); } catch { return false; }
  return isResumable(parseSavedRun(raw), now);
}
